'use client';
import React from 'react';
import { FilterState } from './DiscoverContent';
import { X } from 'lucide-react';

const professions = ['All', 'Students', 'Developers', 'Designers', 'Teachers', 'Doctors', 'Marketers', 'Writers', 'Engineers', 'Finance', 'Entrepreneurs'];
const difficulties = ['All', 'Beginner', 'Intermediate', 'Advanced', 'Expert'];
const durations = ['All', 'Under 15 min', '15-30 min', '30-60 min', '1-3 hours', 'Multi-day'];

interface DiscoverFiltersProps {
  filters: FilterState;
  onUpdateFilter: (key: keyof FilterState, value: string | number) => void;
}

export default function DiscoverFilters({ filters, onUpdateFilter }: DiscoverFiltersProps) {
  const activeFilterCount = [
    filters.profession !== 'All',
    filters.entryType !== 'All',
    filters.difficulty !== 'All',
    filters.duration !== 'All',
    filters.minPrize > 0,
    filters.maxEntry < 1000,
  ].filter(Boolean).length;

  return (
    <div className="filter-panel space-y-5 sticky top-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-foreground">Filters</span>
        {activeFilterCount > 0 && (
          <button
            onClick={() => {
              onUpdateFilter('profession', 'All');
              onUpdateFilter('entryType', 'All');
              onUpdateFilter('difficulty', 'All');
              onUpdateFilter('duration', 'All');
              onUpdateFilter('minPrize', 0);
              onUpdateFilter('maxEntry', 1000);
            }}
            className="flex items-center gap-1 text-xs font-medium" style={{ color: '#F43F5E' }}
          >
            <X size={12} />
            Clear {activeFilterCount}
          </button>
        )}
      </div>

      {/* Entry Type */}
      <div>
        <p className="metric-label mb-2">Entry Type</p>
        <div className="space-y-1.5">
          {['All', 'Free', 'Paid'].map((type) => (
            <label key={`entry-${type}`} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="entryType"
                value={type}
                checked={filters.entryType === type}
                onChange={() => onUpdateFilter('entryType', type)}
                className="accent-primary"
              />
              <span className="text-sm" style={{ color: filters.entryType === type ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                {type}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Profession */}
      <div>
        <p className="metric-label mb-2">Profession</p>
        <select
          value={filters.profession}
          onChange={(e) => onUpdateFilter('profession', e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg outline-none appearance-none"
          style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
        >
          {professions.map((p) => (
            <option key={`prof-filter-${p}`} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {/* Difficulty */}
      <div>
        <p className="metric-label mb-2">Difficulty</p>
        <div className="space-y-1.5">
          {difficulties.map((d) => (
            <label key={`diff-${d}`} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="difficulty"
                value={d}
                checked={filters.difficulty === d}
                onChange={() => onUpdateFilter('difficulty', d)}
                className="accent-primary"
              />
              <span className="text-sm" style={{ color: filters.difficulty === d ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                {d}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Duration */}
      <div>
        <p className="metric-label mb-2">Duration</p>
        <select
          value={filters.duration}
          onChange={(e) => onUpdateFilter('duration', e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg outline-none appearance-none"
          style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
        >
          {durations.map((d) => (
            <option key={`dur-${d}`} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {/* Max Entry Amount */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="metric-label">Max Entry Fee</p>
          <span className="text-xs font-semibold" style={{ color: '#A78BFA' }}>
            {filters.maxEntry === 1000 ? 'Any' : `$${filters.maxEntry}`}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={1000}
          step={5}
          value={filters.maxEntry}
          onChange={(e) => onUpdateFilter('maxEntry', Number(e.target.value))}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
          <span>Free</span>
          <span>$1,000</span>
        </div>
      </div>

      {/* Min Prize Pool */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="metric-label">Min Prize Pool</p>
          <span className="text-xs font-semibold" style={{ color: '#F59E0B' }}>
            {filters.minPrize === 0 ? 'Any' : `$${filters.minPrize}`}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={10000}
          step={100}
          value={filters.minPrize}
          onChange={(e) => onUpdateFilter('minPrize', Number(e.target.value))}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
          <span>$0</span>
          <span>$10,000+</span>
        </div>
      </div>
    </div>
  );
}